/**
 * Live price + stock fetch from our NDS-backed App Proxy at /apps/search/live-pricing.
 *
 * The custom app has exactly one App Proxy (subpath = "search", URL → service /api/...).
 * Both fitment-search and live-pricing share that subpath:
 *   /apps/search/fitment-search → service /api/fitment-search
 *   /apps/search/live-pricing   → service /api/live-pricing
 *
 * WHERE THIS RUNS: the /cart page, and variant pickers outside the cart drawer. The
 * cart DRAWER is deliberately excluded — see the note in start(). Live line prices
 * without the matching cart-line writeback produce a subtotal that disagrees with
 * the lines above it, so the drawer shows Shopify's stored price throughout, which
 * is also what checkout charges.
 *
 * On the surfaces where it does run:
 *   1. Find every <product-price[data-sku]> and <product-inventory[data-sku]> on the page.
 *   2. Dedupe SKUs and fetch them in one request (or chunks of 50 — service-side cap).
 *   3. Replace the price text inside .price spans + the inventory text inside
 *      .product-inventory__text with the live values.
 *   4. Refetch on every Horizon `ThemeEvents.variantUpdate` event so variant pickers stay live.
 *
 * Failure modes are SILENT — Shopify's cached price + the theme's static stock indicator stay
 * exactly as rendered. Customers never see a broken state if our service is down.
 */

import { ThemeEvents } from '@theme/events';

const ENDPOINT = '/apps/search/live-pricing';
const MAX_PER_REQUEST = 50;            // keep in sync with LivePricingController.MAX_SKUS_PER_REQUEST
const FETCH_TIMEOUT_MS = 4000;         // tight — anything slower than this and we keep the cached price
const REQUEST_DEDUPE_MS = 2000;        // collapse duplicate fetches within this window

/** Per-SKU promise cache so two elements asking for the same SKU at the same time share one fetch. */
const inflight = new Map();

/**
 * @param {string[]} skus
 * @param {string} branchId
 * @returns {Promise<Record<string, {price: number|null, qty_in_stock: number|null}>>}
 */
async function fetchQuotes(skus, branchId) {
  if (skus.length === 0) return {};
  const key = `${branchId}|${skus.slice().sort().join(',')}`;
  const cached = inflight.get(key);
  if (cached) return cached;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  const url = new URL(ENDPOINT, window.location.origin);
  url.searchParams.set('sku', skus.join(','));
  if (branchId) url.searchParams.set('branch', branchId);

  const promise = fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => {
      const out = {};
      if (body && Array.isArray(body.quotes)) {
        for (const q of body.quotes) out[q.sku] = q;
      }
      return out;
    })
    .catch(() => ({}))
    .finally(() => {
      clearTimeout(timeout);
      // Keep the dedupe cache for a short window so a flurry of variant-update events doesn't refire.
      setTimeout(() => inflight.delete(key), REQUEST_DEDUPE_MS);
    });

  inflight.set(key, promise);
  return promise;
}

/** Render a price as currency, matching the format already on the page so live replacements
 *  don't change visual weight or width. We sniff the existing rendered text inside the same
 *  product-price element — if it ends in a currency code (e.g. "$21.66 USD"), keep the suffix.
 *  This mirrors Liquid's `money_with_currency` vs `money` filter choice the snippet made. */
function formatPrice(amount, sampleEl) {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  let base;
  try {
    base = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(amount);
  } catch (_e) {
    base = `$${amount.toFixed(2)}`;
  }
  // Preserve the trailing currency code if the original render had one.
  if (sampleEl) {
    const sample = (sampleEl.textContent || '').trim();
    const re = new RegExp(`\\b${currency}\\s*$`);
    if (re.test(sample) && !re.test(base)) base += ` ${currency}`;
  }
  return base;
}

/** Mutate price elements in-place. Skips compare-at-price (we don't get sale prices from RFQ).
 *  Cart context: when data-quantity is set on the wrapper, the visible price spans show the
 *  LINE total (unit × qty) and the optional `[data-live-unit-cost]` span shows "$X.YY ea". */
function applyPrice(productPriceEl, quote) {
  if (quote == null || quote.price == null) return;
  const qtyAttr = parseInt(productPriceEl.dataset.quantity || '0', 10);
  const isCartLine = qtyAttr > 0;
  const lineTotal = isCartLine ? quote.price * qtyAttr : quote.price;

  // .price = PDP/card (theme price-class styling). [data-live-text] = cart line (preserves
  // the cart's own typography classes so we don't inadvertently shrink/restyle the price).
  const priceSpans = productPriceEl.querySelectorAll('.price, [data-live-text]');
  for (const span of priceSpans) {
    const formattedLine = formatPrice(lineTotal, span);
    if (span.textContent.trim() !== formattedLine) {
      span.textContent = formattedLine;
      // Keep `value` attribute in sync — text-component's shimmer pseudo reads from it.
      if (span.tagName === 'TEXT-COMPONENT') span.setAttribute('value', formattedLine);
      span.dataset.livePrice = String(quote.price);
      span.dataset.liveLineTotal = String(lineTotal);
    }
  }
  if (isCartLine) {
    const unit = productPriceEl.querySelector('[data-live-unit-cost]');
    if (unit) {
      const text = `${formatPrice(quote.price, unit)} ea`;
      if (unit.textContent.trim() !== text) unit.textContent = text;
    }
  }
}

/** Mutate inventory elements in-place. Aggregate qty drives the in-stock / low / available
 *  state; per-branch breakdown becomes a small "Available at <City>, <City2>" line under it.
 *  Note: there is no "out of stock" state — every part is orderable; zero on-hand renders as
 *  "Available" so customers can still place the order. */
function applyInventory(productInventoryEl, quote) {
  if (quote == null || quote.qty_in_stock == null) return;
  const qty = Number(quote.qty_in_stock);
  const lowThreshold = Number(productInventoryEl.dataset.lowThreshold || 10);

  const textEl = productInventoryEl.querySelector('.product-inventory__text');
  const iconEl = productInventoryEl.querySelector('.product-inventory__icon');
  if (!textEl || !iconEl) return;

  // Some suppliers contractually bar raw unit counts and any per-warehouse
  // breakdown (§3d, and §3e for named locations). Liquid marks those elements
  // with data-supplier-restricted; see snippets/tvh-gate.liquid.
  const restricted = productInventoryEl.dataset.supplierRestricted === '1';

  let status, label;
  if (qty <= 0) {
    status = 'available';
    label = 'Available';
  } else if (restricted) {
    // Total only, capped — never "Only 3 left", never a per-branch line.
    status = 'in_stock';
    label = qty > 100 ? '100+ in stock' : 'In stock';
  } else if (qty <= lowThreshold) {
    status = 'low';
    label = `Only ${qty} left in stock`;
  } else {
    status = 'in_stock';
    label = `${qty} in stock`;
  }

  // Swap icon class
  iconEl.classList.remove('product-inventory__icon-in_stock', 'product-inventory__icon-low', 'product-inventory__icon-out_of_stock', 'product-inventory__icon-available');
  iconEl.classList.add(`product-inventory__icon-${status}`);

  if (textEl.textContent.trim() !== label) {
    textEl.textContent = label;
    productInventoryEl.dataset.liveQty = String(qty);
  }

  // Per-branch breakdown — render once as a sibling line under the status.
  // Show up to 5 branches by qty (the API already sorts desc and excludes zero-qty branches).
  let branchEl = productInventoryEl.querySelector('.product-inventory__branches');
  const list = restricted ? [] : Array.isArray(quote.branches) ? quote.branches : [];
  if (list.length === 0) {
    if (branchEl) branchEl.remove();
    return;
  }
  if (!branchEl) {
    branchEl = document.createElement('div');
    branchEl.className = 'product-inventory__branches';
    branchEl.style.fontSize = '0.85em';
    branchEl.style.opacity = '0.75';
    branchEl.style.marginTop = '0.25em';
    productInventoryEl.appendChild(branchEl);
  }
  const top = list.slice(0, 5).map((b) => {
    const where = b.city ? `${b.city}, ${b.state}` : b.branch_id;
    return `${where} (${b.qty})`;
  });
  const more = list.length > 5 ? ` +${list.length - 5} more` : '';
  branchEl.textContent = `Available at: ${top.join(' · ')}${more}`;
}

/** Find every live-fetchable element on the page and update it. */
/**
 * Update the PDP's own stock line.
 *
 * <p>The product page does not use a <product-inventory> element — sections/product-info-clean.liquid
 * renders the stock line as a [data-pdp-stock] span INSIDE <product-price data-sku>, server-side from
 * Shopify's stored inventory_quantity. So applyInventory() never sees it, and before this the PDP
 * fetched live stock and had nowhere to put it.
 *
 * Both things this needs are already on the parent element: the SKU, and data-supplier-restricted.
 * No Liquid change required.
 *
 * The markup written here mirrors the Liquid exactly so a live update and a server render are
 * indistinguishable.
 */
const SHIP_DATE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/**
 * The manufacturer's answer for a part we hold none of (JLG today — `quote.supplier`, SY: parts only).
 * Returns null only when there is no supplier answer, so the caller keeps the plain "Available".
 * Never a unit count: JLG itself hides totals (displayTotalAvailability=false).
 *
 * We still order the part in and ship it ourselves (Dan, 2026-10-27), so the manufacturer is never
 * named as the shipper, and a JLG date is JLG's ship date to us, not delivery to the customer.
 * Manufacturer stock reads as our normal "In stock" with the count — but never "on hand", which
 * would claim it is on our shelf (Dan, 2026-10-27). Supplier-restricted products get no count.
 */
function supplierStock(supplier, restricted) {
  if (!supplier) return null;
  if (supplier.orderable === false) {
    // JLG will not take an order for this number (consult-factory / replaced) — a person has to check.
    return { cls: 'pdp__stock--supplier-na', html: '<span class="dot"></span><span>Contact us for availability</span>' };
  }
  const qty = Number(supplier.qty);
  if (qty > 0) {
    return {
      cls: null,
      html: restricted
        ? '<span class="dot"></span><span>In stock</span>'
        : '<span class="dot"></span><span>In stock<span class="pdp__stock-qty"> · ' + qty + ' available</span></span>',
    };
  }
  // Nothing in stock at the manufacturer: say so, with JLG's date when it gives one. JLG's date is its
  // ship date to us; it is shown as the estimated availability (Dan, 2026-10-27).
  let when = '';
  if (supplier.est_ship_date) {
    const d = new Date(supplier.est_ship_date + 'T00:00:00Z');
    if (!isNaN(d)) when = '<span class="pdp__stock-qty"> · est. available ' + SHIP_DATE_FMT.format(d) + '</span>';
  }
  return { cls: 'pdp__stock--available', html: '<span class="dot"></span><span>Backordered' + when + '</span>' };
}

function applyPdpStock(productPriceEl, quote) {
  if (quote == null) return;
  const el = productPriceEl.querySelector('[data-pdp-stock]');
  if (!el) return;

  // "Coming soon" means the variant is not purchasable at all — a Shopify availability state, not a
  // stock count. Live quantity must never overwrite it, or an unbuyable part starts claiming stock.
  if (el.classList.contains('pdp__stock--out')) return;

  // Our own branches come first; the manufacturer only speaks when we hold none (or BC has no record).
  const ours = quote.qty_in_stock == null ? 0 : Number(quote.qty_in_stock);
  const fromSupplier = ours <= 0
    ? supplierStock(quote.supplier, productPriceEl.dataset.supplierRestricted === '1')
    : null;
  if (fromSupplier) {
    el.classList.remove('pdp__stock--available', 'pdp__stock--supplier-na');
    if (fromSupplier.cls) el.classList.add(fromSupplier.cls);
    if (el.innerHTML !== fromSupplier.html) el.innerHTML = fromSupplier.html;
    return;
  }
  el.classList.remove('pdp__stock--supplier-na');
  if (quote.qty_in_stock == null) return;

  const qty = Number(quote.qty_in_stock);
  // Some suppliers contractually bar raw unit counts and per-warehouse detail (§3d/§3e) — the same
  // gate applyInventory() honours, read from the same attribute.
  const restricted = productPriceEl.dataset.supplierRestricted === '1';

  let html;
  if (qty <= 0) {
    // Never "out of stock": every part is orderable, so zero on-hand reads as Available.
    el.classList.add('pdp__stock--available');
    html = '<span class="dot"></span>Available';
  } else {
    el.classList.remove('pdp__stock--available');
    if (restricted) {
      html = qty > 100
        ? '<span class="dot"></span><span>In stock<span class="pdp__stock-qty"> · 100+</span></span>'
        : '<span class="dot"></span><span>In stock</span>';
    } else {
      html = '<span class="dot"></span><span>In stock<span class="pdp__stock-qty"> · ' + qty + ' on hand</span></span>';
    }
  }
  if (el.innerHTML !== html) {
    el.innerHTML = html;
    el.dataset.liveQty = String(qty);
  }
}

async function refreshAll(root = document, branchId = '001') {
  const priceEls = Array.from(root.querySelectorAll('product-price[data-sku]'));
  const invEls = Array.from(root.querySelectorAll('product-inventory[data-sku]'));
  const skuSet = new Set();
  // Skip in-house service SKUs: core deposits (CORE-DEP-*) and the minimum-order fee
  // (MIN-ORDER-FEE-*). Neither is a real catalog part — the NDS pricing endpoint 500s on them —
  // and both are priced by their variant, never live.
  // Also skip parts we sell in a different unit than Equipsoft: IC:PCC6022050 is one 65 ft roll
  // here but priced and stocked per foot there, so a live quote would show (and charge) $/ft.
  const OWN_UNIT_SKUS = new Set(['IC:PCC6022050']);
  const isServiceSku = (s) => s.startsWith('CORE-DEP-') || s.startsWith('MIN-ORDER-FEE-') || OWN_UNIT_SKUS.has(s);
  for (const el of priceEls) {
    const s = (el.dataset.sku || '').trim();
    if (s && !isServiceSku(s)) skuSet.add(s);
  }
  for (const el of invEls) {
    const s = (el.dataset.sku || '').trim();
    if (s && !isServiceSku(s)) skuSet.add(s);
  }
  if (skuSet.size === 0) return;

  // Chunk to honor the service-side cap.
  const skus = Array.from(skuSet);
  const quotes = {};
  for (let i = 0; i < skus.length; i += MAX_PER_REQUEST) {
    const chunk = skus.slice(i, i + MAX_PER_REQUEST);
    const result = await fetchQuotes(chunk, branchId);
    Object.assign(quotes, result);
  }

  for (const el of priceEls) {
    applyPrice(el, quotes[el.dataset.sku]);
    // The PDP keeps its stock line inside the price block rather than in a <product-inventory>.
    applyPdpStock(el, quotes[el.dataset.sku]);
  }
  for (const el of invEls) applyInventory(el, quotes[el.dataset.sku]);

  // On the /cart page only: after we've replaced the displayed line price, the
  // cart's INTERNAL line_price is still whatever Shopify locked in at add-to-cart
  // time — so the Subtotal + Estimated total + checkout charge stay on the old
  // value while the line text shows the new NDS value. Force Shopify to re-pull
  // variant.price into each cart line, then update the totals display in place.
  if (isCartContext()) {
    repriceCartFromQuotes(quotes).catch(() => {});
  }
}

/** After live NDS prices land on the page, repull them into Shopify's cart-line
 *  prices so the subtotal + Estimated total + checkout match. Server-side writeback
 *  has just queued the variant update (ShopifyPriceWriter, 2s coalesce + flush) —
 *  we wait through that window before calling /cart/change.js, otherwise Shopify
 *  would repull the OLD variant.price. /cart/change.js preserves line key, qty,
 *  and properties; the only thing it changes is the unit/line price. */
async function repriceCartFromQuotes(quotes) {
  await new Promise((r) => setTimeout(r, 3000));

  let cart;
  try {
    cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
  } catch (_e) {
    return;
  }
  if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

  // Find lines whose stored line.price (cents) differs from NDS quote price by >= 1c.
  const tasks = [];
  for (const line of cart.items) {
    if (!line.sku) continue;
    const q = quotes[line.sku];
    if (!q || q.price == null) continue;
    const ndsCents = Math.round(q.price * 100);
    if (Math.abs(ndsCents - line.price) >= 1) {
      tasks.push({ key: line.key, qty: line.quantity });
    }
  }
  if (tasks.length === 0) return;

  // Sequential per the no-concurrency-on-cart-mutations rule. Shopify's /cart/change.js
  // is a no-op if the locked variant.price already matches what we'd pull — so this
  // also self-heals on a second pass if writeback flushed late.
  let lastCart = null;
  for (const t of tasks) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: t.key, quantity: t.qty }),
      });
      if (r.ok) lastCart = await r.json();
    } catch (_e) {
      /* one line's failure shouldn't kill the rest */
    }
  }
  if (!lastCart) return;

  updateSubtotalDisplays(lastCart);

  // Announce the reprice. This runs ~3s after /cart loads and can move the cart across the
  // minimum-order threshold long after assets/min-order-fee.js already decided — without this a
  // cart repriced from $36 to $33 never gets its fee, and one repriced from $33 to $37 keeps a fee
  // it no longer owes. A distinct event name on purpose: this module listens to `cart:refresh`
  // itself, so reusing that would re-enter refreshAll in an unbounded loop.
  document.dispatchEvent(new CustomEvent('cart:reprice', { detail: { cart: lastCart } }));
}

/** Update the cart-summary "Subtotal" row + "Estimated total" element in place from
 *  the cart JSON. Both are rendered server-side from cart.items_subtotal_price /
 *  cart.total_price (see snippets/cart-summary.liquid). */
function updateSubtotalDisplays(cart) {
  // Estimated total has the data attr.
  const totalEl = document.querySelector('[data-cart-subtotal]');
  if (totalEl) {
    const formatted = formatPrice(cart.total_price / 100, totalEl);
    if (totalEl.textContent.trim() !== formatted) {
      totalEl.textContent = formatted;
      if (totalEl.tagName === 'TEXT-COMPONENT') totalEl.setAttribute('value', formatted);
    }
  }
  // "Subtotal" row in cart-summary.liquid is the first non-discount, non-muted val.
  const labelEls = document.querySelectorAll('.summary-row__label');
  for (const labelEl of labelEls) {
    const text = (labelEl.textContent || '').trim().toLowerCase();
    if (text !== 'subtotal') continue;
    const valEl = labelEl.parentElement && labelEl.parentElement.querySelector('.summary-row__val');
    if (!valEl) break;
    // Net out the minimum-order fee, exactly as snippets/cart-summary.liquid does when it renders
    // this row server-side. The fee is a real cart line, so items_subtotal_price includes it — and
    // the summary itemises it on its own row underneath. Leaving it in both places would make
    // Subtotal + fee overshoot the Estimated total by $10.
    const feeCents = cart.items.reduce((sum, line) => {
      const sku = String(line.sku || '');
      const isFee = Boolean(line.properties && line.properties._min_order_fee) || sku.startsWith('MIN-ORDER-FEE-');
      return isFee ? sum + line.final_line_price : sum;
    }, 0);
    const formatted = formatPrice((cart.items_subtotal_price - feeCents) / 100, valEl);
    if (valEl.textContent.trim() !== formatted) valEl.textContent = formatted;
    break;
  }
}

/** Update a specific element's data-sku and refetch — used on Horizon's variantUpdate event,
 *  which fires when the variant picker changes the active variant. */
function handleVariantUpdate(event) {
  const detail = event && event.detail;
  if (!detail || !detail.resource) return;
  const newSku = detail.resource.sku;
  if (!newSku) return;

  // Find every <product-price>/<product-inventory> in the same section as the picker.
  const section = event.target && event.target.closest
    ? event.target.closest('.shopify-section, dialog')
    : document;
  if (!section) return;

  for (const el of section.querySelectorAll('product-price[data-sku], product-inventory[data-sku]')) {
    el.dataset.sku = newSku;
  }

  // Same rule as the drawer trigger above: never put a live price on a cart line
  // whose subtotal we are not also repricing. A variant change inside the drawer
  // would otherwise re-open the mismatch one line at a time. On /cart the reprice
  // runs, so the refresh is safe there.
  const inCartDrawer =
    section.matches &&
    (section.matches('dialog, cart-drawer') || !!section.querySelector('cart-drawer, [data-cart-line]'));
  if (inCartDrawer && !isCartContext()) return;

  refreshAll(section).catch(() => {});
}

/** Product detail pages. Shopify's PDP path is always /products/<handle>, optionally behind a
 *  locale prefix (/en/products/...). */
function isProductContext() {
  return /(^|\/)products\/[^/]+\/?$/.test(location.pathname);
}

function isCartContext() {
  // The Theme global is declared with const in an inline <script> in scripts.liquid, so it
  // doesn't attach to window — ES modules can't see it. Use the URL instead (Shopify cart
  // page is always /cart, with optional locale prefix like /en/cart).
  return /(^|\/)cart\/?$/.test(location.pathname);
}

function start() {
  // CART + PDP. This used to be cart-only, and the reason was NDS: every lookup fanned an RFQ
  // across ~18 branches, so one collection view cost ~24 SKUs x 18 calls against DIS's rate
  // limit, and browse paths were deliberately left on Shopify's stored price.
  //
  // Equipsoft removes that cost entirely. A whole batch of SKUs is answered in TWO filtered
  // reads — one for price and company quantity, one for the per-branch breakdown, because a
  // PartNo filter returns every branch row for that part in a single call. Measured 2026-09-01:
  // 468 SKUs in 4.4 s. There is no fan-out left to ration.
  //
  // What the PDP gains is STOCK, not price: the backend serves a null price while
  // equipsoft.live-price-enabled is off, and applyPrice() no-ops on null, so Shopify's stored
  // price stays on screen. That matters more than it sounds — the "stored price, refreshed
  // daily" this comment used to promise has not been true since the nightly catalog sync was
  // killed on 2026-05-09, so browse-path stock was months stale with nothing refreshing it.
  //
  // The cart DRAWER stays excluded for an unrelated reason that still holds — see below.
  if (isCartContext() || isProductContext()) {
    // The cart line items may not be in the DOM yet when this module evaluates —
    // fetchpriority="low" + Horizon's section-rendering ordering means refreshAll()
    // sometimes runs before <product-price> elements exist, then bails silently on
    // skuSet.size === 0. Wait for at least one to appear before firing.
    if (document.querySelector('product-price[data-sku]')) {
      refreshAll().catch(() => {});
    } else {
      const obs = new MutationObserver((_muts, observer) => {
        if (document.querySelector('product-price[data-sku]')) {
          observer.disconnect();
          refreshAll().catch(() => {});
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => obs.disconnect(), 5000);
    }
  }
  // The cart DRAWER deliberately does NOT get live prices.
  //
  // It used to: `dialog:opened` fired refreshAll() on the drawer, which rewrote each
  // line's displayed price to the live NDS value. But repriceCartFromQuotes() — the
  // step that pushes those prices into Shopify's cart lines so the totals agree — is
  // gated to the /cart page (see the isCartContext() check in refreshAll). So the
  // drawer showed live line prices stacked above a subtotal, and a checkout charge,
  // computed from the OLD stored price. Two lines at $56.25 and $18.00 over a
  // subtotal of $64.55. The customer was shown a number they would not be charged,
  // at the exact moment they decide to buy.
  //
  // Flipping the gate is the obvious fix and the wrong one here: calling
  // /cart/change.js while the drawer is mid-morph re-triggers the known
  // drawer-reconcile race. So the drawer now shows Shopify's stored price for both
  // the lines and the subtotal — internally consistent, and equal to what checkout
  // actually charges. /cart remains the surface that fetches live NDS prices and
  // writes them back, where the reprice already works.
  //
  // Side benefit: this removes a whole NDS fan-out from the add-to-cart path. Live
  // pricing now fires once, on /cart, instead of on every drawer open.
  //
  // Cart morph re-renders the line items — refresh after any cart change, but only
  // on /cart. Without the guard this listener re-introduces the mismatch through the
  // back door whenever the drawer updates.
  document.addEventListener('cart:refresh', () => {
    if (isCartContext()) refreshAll().catch(() => {});
  });
  // Variant pickers on a cart-drawer item still need to refresh on change.
  document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
